import { PageController } from '@client/components/_core/layout/PageController';
import { AiAssistantButton } from '@client/components/_shared/ai/AiAssistantButton';
import { Button as ModernButton } from '@client/components/_ui/data-entry/ButtonModern';
import { Alerts } from '@client/components/_ui/display/Alerts';
import { BetaTag } from '@client/components/_ui/display/BetaTag';
import { UpgradeTag } from '@client/components/_ui/display/UpgradeTag';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { LazyPanelFallback } from '@client/components/_ui/feedback/LazyPanelFallback';
import { message } from '@client/components/_ui/feedback/message';
import { DetectedBreachAlert } from '@client/components/compliance/DetectedBreachAlert';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { usePendingTimelineCloseGuard } from '@client/hooks/business/milestone/usePendingTimelineCloseGuard';
import { useIsLiteOrg } from '@client/hooks/business/org/useIsLiteOrg';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useUser } from '@client/hooks/business/user/useUser';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useFeatureFlag } from '@client/hooks/useFeatureFlag';
import { selectOrg, selectUser, useAuthStore } from '@client/stores/AuthStore';
import { ICONS } from '@client/styles/theme';
import { DETAIL_RECORD_QUERY_OPTIONS } from '@client/trpc/query-options';
import type { FieldError } from '@client/types/error';
import type { ShareableRouteContext } from '@client/types/router';
import { capture } from '@client/utils/analytics';
import { TokenRefreshManager } from '@client/utils/auth/TokenRefreshManager';
import { showLiteUpgradePopover } from '@client/utils/domain/lite';
import { useContentReadyMark } from '@client/utils/performance/content-ready';
import { INCIDENT_STATUSES_KEYS, ORG_ROLES } from '@shared/constants';
import type { IncidentAiAnalysisStatus } from '@shared/prisma/enums';
import { createFileRoute, Outlet, useLocation } from '@tanstack/react-router';
import { Skeleton, Tabs } from 'antd';
import { Bot, CheckCircle, Download, Flag, WandSparkles } from 'lucide-react';
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { match } from 'ts-pattern';
import { runDetailRoutePrefetchWhenAuthorised, shouldPrefetchDetailRouteData } from '../../../detail-route-prefetch';
import {
    getIncidentAiAnalysisPollingTransition,
    getIncidentAiAnalysisRefetchInterval,
    INCIDENT_AI_ANALYSIS_BOOTSTRAP_WINDOW_MS,
} from './aiAnalysisPolling';
import { Header } from './Header';
import { useExportIncident, useValidateIncidentBeforeClosing } from './hooks';
import { IncidentAiAnalysisPollingContext } from './IncidentAiAnalysisPollingContext';
import type { IncidentAiReportAction } from './IncidentAiReport';
import { useObligationsDrawerStore } from './IncidentDetailsManage/obligationsDrawerStore';
import { prefetchIncidentDetails } from './incident-details-prefetch';
import { canEditIncident, hasIncidentUpdateAccess } from './permissions';
import { incidentFormStore } from './store';

// Drawers and panels that are closed on arrival: lazy, and mounted only from first open
// (kept mounted afterwards so close transitions still play — same pattern as the lodgement
// route's SaveClaimDraftModal). Their chunks load on demand instead of inside this route's
// LCP-critical chunk, and none renders anything until opened.
const IncidentAiReport = lazy(() =>
    import('./IncidentAiReport').then((module) => ({ default: module.IncidentAiReport })),
);
const CloseIncidentDrawer = lazy(() =>
    import('./CloseIncidentDrawer').then((module) => ({ default: module.CloseIncidentDrawer })),
);
const EntityChatPanel = lazy(() =>
    import('@client/components/entity-chat').then((module) => ({ default: module.EntityChatPanel })),
);
const IncidentFlagManage = lazy(() =>
    import('@client/components/incidents/IncidentFlagManage').then((module) => ({
        default: module.IncidentFlagManage,
    })),
);

export const Route = createFileRoute('/console/$orgId/compliance/incidents/details/$id')({
    staticData: {
        metadata: {
            title: 'Incident details',
        },
        ui: {
            widthVariant: 'default',
        },
        entity: 'incident',
    },
    params: {
        parse({ id }) {
            return { id: BigInt(id) };
        },
    },
    beforeLoad: ({ context, params, preload, abortController }): ShareableRouteContext => {
        // Kick off the LCP-critical `getIncident` (and the manage tab's static
        // lookups) while the route's chunks are still being parsed and the auth
        // gate is still rendering — otherwise none of them start until the
        // component mounts. Same pattern as `~console.$orgId/~index.tsx`.
        // On a cold email deep link the session is still restoring here, so the
        // prefetch is deferred until the auth store becomes authorised. Hover-intent
        // preloads never defer (each hovered row would queue a speculative burst
        // behind the next token refresh); leaving the match aborts the deferral.
        runDetailRoutePrefetchWhenAuthorised(
            () => {
                const authState = useAuthStore.getState();
                const user = selectUser(authState);
                return shouldPrefetchDetailRouteData({
                    hasValidToken: authState.hasValidToken({
                        minValidityMs: TokenRefreshManager.REFRESH_BUFFER_MS,
                    }),
                    userRoles: user?.roles,
                    allowedRoles: [ORG_ROLES.USER, ORG_ROLES.SHARED_EDIT_USER, ORG_ROLES.ORG_ADMIN],
                    hydratedOrg: selectOrg(authState),
                    routeOrgId: params.orgId,
                    requiredModule: 'compliance',
                });
            },
            () => prefetchIncidentDetails(context.trpcUtils, params.id),
            { defer: !preload, signal: abortController.signal },
        );
        return undefined;
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <IncidentDetails />
        </PageController>
    );
}

/** Detail-page tab keys — also the `~manage` / `~library` / `~history` sub-route segments. */
const INCIDENT_TABS = ['manage', 'library', 'history'] as const;

function IncidentDetails() {
    const orgId = useOrgId();
    const enableIncidentAiReport = useFeatureFlag('enable-incident-ai-report');
    const enableAiChat = useFeatureFlag('enable-ai-chat');
    const isLiteOrg = useIsLiteOrg();
    const user = useUser();
    const { trpcUtils, trpc } = useTrpc();
    const { id } = Route.useParams();
    const { pathname } = useLocation();
    const updateIncidentMutation = trpc.incident.updateIncident.useMutation();
    const [incidentAiReportAction, setIncidentAiReportAction] = useState<IncidentAiReportAction>(['close']);
    const [aiAnalysisPollingBootstrapUntil, setAiAnalysisPollingBootstrapUntil] = useState<number | null>(null);
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [isIncidentFlagManageOpen, setIsIncidentFlagManageOpen] = useState(false);
    // First-open latches for the lazy panels above: set alongside the open state so the
    // panel mounts (and its chunk loads) on the same render the user opens it.
    const hasOpenedAiReportRef = useRef(false);
    const hasOpenedCloseDrawerRef = useRef(false);
    const hasOpenedFlagManageRef = useRef(false);
    const hasOpenedChatRef = useRef(false);

    const navigate = useNavigate();

    const [exportIncident, { loading: exportIncidentLoading }] = useExportIncident();

    const { validateRequiredFields } = useValidateIncidentBeforeClosing();
    const { blockCloseIfPendingTimeline, loading: pendingTimelineLoading } = usePendingTimelineCloseGuard({
        entity: 'incident',
        entityId: id,
    });

    const [requiredFieldsErrors, setRequiredFieldsErrors] = useState<FieldError[]>([]);
    const [open, setOpen] = useState(false);

    const openObligationsPlain = useObligationsDrawerStore((s) => s.openPlain);
    const closeObligationsDrawer = useObligationsDrawerStore((s) => s.close);

    // This detail route is reused across `$id` navigations without remounting, so the close-incident
    // drawer's open state would otherwise carry over to the next incident (and show its frozen field
    // set). Dismiss it whenever the incident changes so each incident starts clean (CUR-306). Also
    // reset the obligations slider store — it scopes visibility by incident id, but leaving stale
    // open/type metadata behind lets it revive if the user returns to this incident (CR-1).
    useEffect(() => {
        setOpen(false);
        setRequiredFieldsErrors([]);
        closeObligationsDrawer();
    }, [id, closeObligationsDrawer]);

    // Fetch incident data with tRPC query
    const incidentQuery = trpc.incident.getIncident.useQuery(
        { id },
        {
            enabled: !!id,
            ...DETAIL_RECORD_QUERY_OPTIONS,
            throwOnError(error) {
                message.destroy();

                match(error.data?.errorId)
                    .with('recordNotFound', () => {
                        void errorMessage
                            .showAsync(
                                "The incident you are trying to access either doesn't exist or you don't have permissions to access it.",
                            )
                            .then(() => {
                                navigate({ to: '/console/$orgId/compliance/incidents', params: { orgId } });
                            });
                    })
                    .otherwise(() => {
                        errorMessage.show(error);
                    });

                return false;
            },
        },
    );

    // Poll the relational AI analysis status so the banner / header reflect background-job progress.
    // When the status transitions out of PROCESSING/PENDING, refresh the incident so
    // `unresolvedAiBreachCount` picks up the trigger-driven update.
    const aiAnalysisQuery = trpc.incident.getIncidentAiAnalysis.useQuery(
        { incidentId: id },
        {
            enabled: !!id,
            ...DETAIL_RECORD_QUERY_OPTIONS,
            refetchInterval: (query) => {
                return getIncidentAiAnalysisRefetchInterval({
                    status: query.state.data?.status,
                    bootstrapUntil: aiAnalysisPollingBootstrapUntil,
                });
            },
            refetchIntervalInBackground: false,
        },
    );

    const prevAiAnalysis = useRef<{
        status: IncidentAiAnalysisStatus | null | undefined;
        uuid: string | null | undefined;
    }>({
        status: undefined,
        uuid: undefined,
    });
    useEffect(() => {
        const prev = prevAiAnalysis.current;
        const currStatus = aiAnalysisQuery.data?.status;
        const currUuid = aiAnalysisQuery.data?.uuid;
        const transition = getIncidentAiAnalysisPollingTransition({
            previous: prev,
            current: { status: currStatus, uuid: currUuid },
            bootstrapUntil: aiAnalysisPollingBootstrapUntil,
            now: Date.now(),
        });

        if (transition.shouldInvalidateIncident) {
            void trpcUtils.incident.getIncident.invalidate({ id });
            setAiAnalysisPollingBootstrapUntil(transition.bootstrapUntil);
        }
        prevAiAnalysis.current = {
            status: currStatus,
            uuid: currUuid,
        };
    }, [aiAnalysisPollingBootstrapUntil, aiAnalysisQuery.data?.status, aiAnalysisQuery.data?.uuid, id, trpcUtils]);

    const bootstrapIncidentAiAnalysisPolling = useCallback(
        async (windowMs = INCIDENT_AI_ANALYSIS_BOOTSTRAP_WINDOW_MS) => {
            const nextBootstrapUntil = Date.now() + windowMs;
            setAiAnalysisPollingBootstrapUntil((current) =>
                current != null && current > nextBootstrapUntil ? current : nextBootstrapUntil,
            );
            await trpcUtils.incident.getIncidentAiAnalysis.invalidate({ incidentId: id });
        },
        [id, trpcUtils],
    );

    const { formData: incident, save } = incidentFormStore.useInit({
        entity: 'incident',
        data: incidentQuery.data,
        isLoading: incidentQuery.isLoading,

        async onReload() {
            await incidentQuery.refetch();
        },

        async onSave(currentValue, newValue) {
            try {
                const result = await updateIncidentMutation.mutateAsync({ id: currentValue.id, set: newValue });

                if (newValue.status) {
                    capture('incident_status_changed', {
                        entity_id: currentValue.id.toString(),
                        to_status: newValue.status,
                        module: 'compliance',
                    });
                }

                if (result.analysisRequested) {
                    await bootstrapIncidentAiAnalysisPolling();
                }

                await trpcUtils.incident.invalidate();

                return { success: true };
            } catch (e) {
                return { success: false, error: e };
            }
        },
    });

    const canEdit = useMemo(() => {
        if (!incident) {
            return false;
        }

        return canEditIncident(user, incident);
    }, [incident, user]);
    const canChangeStatus = useMemo(() => {
        if (!incident) {
            return false;
        }

        return hasIncidentUpdateAccess(user, incident);
    }, [incident, user]);

    const unresolvedBreachCount = incident?.unresolvedAiBreachCount ?? 0;

    const activeTab = INCIDENT_TABS.find((tab) => pathname.split('/')[7] === tab) ?? 'manage';

    // LCP guardrail (CUR-1036, task 0.1): the page's content is ready once the current
    // incident record has resolved — the static chrome below painting earlier must not
    // move this.
    useContentReadyMark(id === incident?.id);

    const openIncident = async () => {
        if (!canChangeStatus) {
            errorMessage.show('You do not have permission to update this incident.');
            return;
        }

        await save({ status: INCIDENT_STATUSES_KEYS.open });
        // Auto-invalidation handles cache refresh
    };

    const closeIncident = async () => {
        if (!incident) {
            return;
        }

        if (!canEdit) {
            return;
        }

        if (await blockCloseIfPendingTimeline()) {
            return;
        }

        // A breach-type incident with no linked obligations cannot be closed (CUR-175 req 2).
        // Divert to the Manage tab's obligations slider instead of the close drawer, which can't
        // capture obligations.
        if (incident.isBreachWithoutObligations) {
            openObligationsPlain(incident.id);
            navigate({
                to: '/console/$orgId/compliance/incidents/details/$id/manage',
                params: { orgId, id: incident.id },
            });
            return;
        }

        const requiredFieldsErrors = validateRequiredFields(incident);

        if (requiredFieldsErrors.length) {
            setRequiredFieldsErrors(requiredFieldsErrors);
        }

        if (requiredFieldsErrors.length || !incident.closureSummary) {
            hasOpenedCloseDrawerRef.current = true;
            setOpen(true);
            return;
        }

        await save({ status: INCIDENT_STATUSES_KEYS.closed });
        // Auto-invalidation handles cache refresh
    };

    return (
        <IncidentAiAnalysisPollingContext.Provider value={bootstrapIncidentAiAnalysisPolling}>
            {hasOpenedAiReportRef.current && (
                <Suspense fallback={<LazyPanelFallback />}>
                    <IncidentAiReport
                        action={incidentAiReportAction}
                        canManageAnalysis={canChangeStatus}
                        onClose={() => {
                            setIncidentAiReportAction(['close']);
                        }}
                    />
                </Suspense>
            )}
            {incident && canEdit && hasOpenedCloseDrawerRef.current && (
                <Suspense fallback={<LazyPanelFallback />}>
                    <CloseIncidentDrawer
                        open={open}
                        onSave={(data) => save({ status: INCIDENT_STATUSES_KEYS.closed, ...data })}
                        onClose={() => {
                            setRequiredFieldsErrors([]);
                            setOpen(false);
                        }}
                        incident={incident}
                        errors={requiredFieldsErrors}
                    />
                </Suspense>
            )}
            {/* Static chrome before data (CUR-1036, task 2.5): the header is entirely
                record-derived, so pre-data it gets a dimension-reserving skeleton in the
                same `mb-4` slot instead of being absent (which shifted everything below
                when the record landed). */}
            {id === incident?.id ? <Header /> : <Skeleton active title paragraph={{ rows: 1 }} className="mb-4" />}
            {id === incident?.id && hasOpenedFlagManageRef.current ? (
                <Suspense fallback={<LazyPanelFallback />}>
                    <IncidentFlagManage
                        isOpen={isIncidentFlagManageOpen}
                        onClose={() => setIsIncidentFlagManageOpen(false)}
                        incidentId={incident.id}
                        isReadOnly={!canEdit}
                    />
                </Suspense>
            ) : null}
            {id === incident?.id && !!unresolvedBreachCount && enableIncidentAiReport && (
                <DetectedBreachAlert
                    title={
                        <>
                            {unresolvedBreachCount} AI-detected breach
                            {unresolvedBreachCount > 1 ? 'es' : ''} require
                            {unresolvedBreachCount === 1 ? 's' : ''} attention
                        </>
                    }
                    description={
                        canChangeStatus ? (
                            <span className="text-sm">
                                Our AI has automatically identified {unresolvedBreachCount === 1 ? 'a' : ''} potential
                                compliance breach{unresolvedBreachCount > 1 ? 'es' : ''} in this incident. Please review
                                and assign {unresolvedBreachCount === 1 ? 'the obligation' : 'obligations'} to track
                                remediation.
                            </span>
                        ) : (
                            <span className="text-sm">
                                Our AI has automatically identified {unresolvedBreachCount === 1 ? 'a' : ''} potential
                                compliance breach{unresolvedBreachCount > 1 ? 'es' : ''} in this incident.
                            </span>
                        )
                    }
                    actionLabel={canChangeStatus ? 'Review breaches' : 'View breaches'}
                    actionType={canChangeStatus ? 'danger-filled' : 'secondary'}
                    onAction={() => {
                        hasOpenedAiReportRef.current = true;
                        setIncidentAiReportAction(['open', id]);
                        capture('unresolved_breach_alert_clicked', {
                            incident_id: id.toString(),
                            unresolved_count: unresolvedBreachCount,
                            org_id: orgId.toString(),
                        });
                    }}
                    className="mb-6"
                />
            )}
            {id === incident?.id ? (
                <div className="mb-4 flex items-center gap-2">
                    <ModernButton.Icon
                        icon={<Flag />}
                        color="red"
                        bordered
                        tooltip="Manage flags"
                        onClick={() => {
                            hasOpenedFlagManageRef.current = true;
                            setIsIncidentFlagManageOpen(true);
                        }}
                    />

                    <ModernButton.Icon
                        icon={<Download />}
                        color="purple"
                        bordered
                        loading={exportIncidentLoading}
                        tooltip="Download incident"
                        onClick={() => {
                            void exportIncident(id);
                        }}
                    />

                    <div className="flex grow items-center justify-end gap-2">
                        <AiAssistantButton
                            entityType="incident"
                            entityId={incident.id}
                            entityName="Incident"
                            outputFormat="markdown"
                        />
                        {enableAiChat &&
                            (isLiteOrg ? (
                                // Curium Lite (CUR-379): AI chat is premium — crowned, opens the contact page.
                                <ModernButton
                                    type="secondary"
                                    icon={<Bot />}
                                    onClick={(event) => showLiteUpgradePopover('incident-ai-chat', event.currentTarget)}
                                    aria-label="AI chat — upgrade to unlock"
                                >
                                    AI Chat
                                    <UpgradeTag className="mr-0" />
                                </ModernButton>
                            ) : (
                                <ModernButton
                                    type="secondary"
                                    icon={<Bot />}
                                    badge={<BetaTag />}
                                    onClick={() => {
                                        hasOpenedChatRef.current = true;
                                        setIsChatOpen(!isChatOpen);
                                        capture('incident_chat_toggled', {
                                            incident_id: id.toString(),
                                            chat_open: !isChatOpen,
                                            org_id: orgId.toString(),
                                        });
                                    }}
                                >
                                    AI Chat
                                </ModernButton>
                            ))}
                        {enableIncidentAiReport && (
                            <ModernButton
                                type="secondary"
                                icon={<WandSparkles />}
                                badge={<BetaTag />}
                                onClick={() => {
                                    // Track analyze incident button click
                                    capture('analyze_incident_clicked', {
                                        incident_id: id.toString(),
                                        incident_status: incident.status ?? undefined,
                                        org_id: orgId.toString(),
                                    });
                                    hasOpenedAiReportRef.current = true;
                                    setIncidentAiReportAction(['open', id]);
                                }}
                            >
                                {canChangeStatus ? 'Analyse incident' : 'AI analysis'}
                            </ModernButton>
                        )}
                        {(incident.status === INCIDENT_STATUSES_KEYS.closed ? canChangeStatus : canEdit) &&
                            (incident.status === INCIDENT_STATUSES_KEYS.closed ? (
                                <ModernButton type="primary" onClick={openIncident}>
                                    Open incident
                                </ModernButton>
                            ) : (
                                <ModernButton
                                    type="primary"
                                    loading={pendingTimelineLoading}
                                    icon={<CheckCircle />}
                                    onClick={closeIncident}
                                >
                                    Close incident
                                </ModernButton>
                            ))}
                    </div>
                </div>
            ) : (
                // Static chrome before data (CUR-1036, task 2.5): reserve the action row in
                // its final position with the two always-present controls, disabled until
                // the record resolves. Right-aligned data-dependent buttons stay absent —
                // their appearance cannot shift the left-anchored content or the row height.
                <div className="mb-4 flex items-center gap-2">
                    <ModernButton.Icon icon={<Flag />} color="red" bordered disabled tooltip="Manage flags" />
                    <ModernButton.Icon
                        icon={<Download />}
                        color="purple"
                        bordered
                        disabled
                        tooltip="Download incident"
                    />
                    <div className="flex grow items-center justify-end gap-2" />
                </div>
            )}
            {/* Static chrome before data (CUR-1036, task 2.5): the tab strip's labels are
                static, so it renders immediately; only the tab CONTENT waits for the record
                (the manage tab reads the form store the record populates). Tab clicks
                pre-data just navigate between equally-pending tabs — harmless. */}
            <Tabs
                type="line"
                destroyOnHidden
                activeKey={activeTab}
                tabBarExtraContent={{
                    left: (
                        <div className="mx-4 size-4">
                            {id === incident?.id ? <Alerts id={id} entity="incident" key={id} /> : null}
                        </div>
                    ),
                }}
                onTabClick={(key) => {
                    navigate({
                        to: `/console/$orgId/compliance/incidents/details/$id/${key}`,
                        params: { orgId, id },
                    });
                }}
                items={[
                    {
                        key: 'manage',
                        label: (
                            <span className="flex items-center gap-2">
                                <ICONS.manage size={16} />
                                Manage incident
                            </span>
                        ),
                        children: id === incident?.id ? <Outlet /> : <Skeleton active className="mt-4" />,
                    },
                    {
                        key: 'library',
                        label: (
                            <span className="flex items-center gap-2">
                                <ICONS.library size={16} />
                                Library
                            </span>
                        ),
                        children: id === incident?.id ? <Outlet /> : <Skeleton active className="mt-4" />,
                    },
                    {
                        key: 'history',
                        label: (
                            <span className="flex items-center gap-2">
                                <ICONS.history size={16} />
                                History
                            </span>
                        ),
                        children: id === incident?.id ? <Outlet /> : <Skeleton active className="mt-4" />,
                    },
                ]}
            />
            {incident && !isLiteOrg && hasOpenedChatRef.current && (
                <Suspense fallback={<LazyPanelFallback />}>
                    <EntityChatPanel
                        isOpen={isChatOpen}
                        onClose={() => setIsChatOpen(false)}
                        entityType="incident"
                        entityId={incident.id}
                        entityName={`Incident #${incident.id}`}
                        entityDisplayName="incident"
                        onToolCall={(toolName) => {
                            if (toolName === 'createTask') {
                                trpcUtils.task.invalidate();
                            }
                        }}
                    />
                </Suspense>
            )}
        </IncidentAiAnalysisPollingContext.Provider>
    );
}
